/**
 * FadeSpecsTable — custom element for the "Fade Specs Table" section.
 *
 * Responsibilities:
 *  1. Table collapse/expand — hides rows beyond `data-collapse-after` and
 *     wires any button with data-role="expand" to reveal them.
 *  2. Fade-in — IntersectionObserver toggles .is-visible on .fst__fade
 *     elements. Repeats every time the element re-enters the viewport.
 */

if (!customElements.get('fade-specs-table')) {
  class FadeSpecsTable extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        this._initCollapse();
        this._initFade();
      });
    }

    // ── 1. Table collapse / expand (toggle) ──────────────────────────────

    _initCollapse() {
      const after = parseInt(this.dataset.collapseAfter || '0', 10);
      if (!after) return;

      // Only target tbody rows so header rows are never touched
      const rows = [...this.querySelectorAll('.fst__table-inner tbody tr')];
      if (rows.length <= after) return;

      this._collapsibleRows = rows.slice(after);
      this._expanded = false;

      // Initially hide rows
      this._collapsibleRows.forEach((r) => r.classList.add('fst__row--hidden'));

      // Wire toggle button(s)
      this.querySelectorAll('.fst__btn[data-role="expand"]').forEach((btn) => {
        const labelExpand   = btn.dataset.labelExpand   || btn.textContent.trim();
        const labelCollapse = btn.dataset.labelCollapse || 'Compact table';

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this._expanded = !this._expanded;

          if (this._expanded) {
            this._collapsibleRows.forEach((r) => r.classList.remove('fst__row--hidden'));
            btn.textContent = labelCollapse;
          } else {
            this._collapsibleRows.forEach((r) => r.classList.add('fst__row--hidden'));
            btn.textContent = labelExpand;
          }
        });
      });
    }

    // ── 2. Fade-in ────────────────────────────────────────────────────────

    _initFade() {
      if (this.dataset.fade !== 'true') return;

      const els = [...this.querySelectorAll('.fst__fade')];
      if (!els.length) return;

      // Stagger entrance delays
      els.forEach((el, i) => {
        el.style.transitionDelay = (i * 120) + 'ms';
      });

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-visible', entry.isIntersecting);
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
      );

      els.forEach((el) => observer.observe(el));
    }
  }

  customElements.define('fade-specs-table', FadeSpecsTable);
}
